import { Box, Typography } from '@mui/material';
import {
  CALCULATION_RESULT_PRECISION,
  MAIN_CURRENCY,
  PLATFORM_FEE_CURRENCY,
} from '@okletsplay/core';
import classNames from 'classnames';
import React, { FC } from 'react';
import CountUp from 'react-countup';
import { useStyles } from './PrizeCountUp.styles';
import {
  PRIZE_ANIMATION_DELAY,
  PRIZE_ANIMATION_DURATION,
  PRIZE_DECIMAL_CHARACTER,
  PRIZE_MIN_DECIMAL_PLACES,
} from 'modules/tournaments/constants';
import { PrizeItem } from './prize-item.interface';
import { CurrencyType } from 'modules/payments/constants';
import BigNumber from 'bignumber.js';

const CURRENCY_SETTINGS = {
  [CurrencyType.CryptoSecondary]: {
    prefix: '',
    suffix: ` ${PLATFORM_FEE_CURRENCY}`,
  },
  [CurrencyType.CryptoPrimary]: {
    prefix: '',
    suffix: ` ${MAIN_CURRENCY}`,
  },
  [CurrencyType.Usd]: {
    prefix: '($',
    suffix: ')',
  },
};

interface PrizeCountUpProps {
  prize: PrizeItem;
  isResultAvailable: boolean;
}

export const PrizeCountUp: FC<PrizeCountUpProps> = ({ prize, isResultAvailable }) => {
  const { classes } = useStyles();

  const amountDecimalPlaces = Math.max(
    new BigNumber(prize.amount).decimalPlaces() ?? 0,
    PRIZE_MIN_DECIMAL_PLACES
  );

  const isUsd = prize.currency === CurrencyType.Usd;

  const decimalsToShow = isUsd
    ? PRIZE_MIN_DECIMAL_PLACES
    : Math.min(CALCULATION_RESULT_PRECISION, amountDecimalPlaces);

  // Note: `prefix` and `suffix` are rendered separately as CountUp does not support new lines for tight spaces with wide `end`.
  const { prefix, suffix } = CURRENCY_SETTINGS[prize.currency];

  const textClasses = classNames(classes.prizeText, { [classes.smallerText]: isUsd });

  return (
    <Box
      className={classNames(classes.root, { [classes.fadeIn]: isResultAvailable })}
      sx={{ display: 'flex', flexWrap: isUsd ? 'no-wrap' : 'wrap' }}
    >
      {prefix ? <Typography className={textClasses}>{prefix}</Typography> : null}

      <Box sx={{ pl: prefix ? 1 : 0, pr: suffix ? 1 : 0 }}>
        <CountUp
          className={textClasses}
          end={prize.amount}
          decimal={PRIZE_DECIMAL_CHARACTER}
          decimals={decimalsToShow}
          duration={PRIZE_ANIMATION_DURATION}
          delay={PRIZE_ANIMATION_DELAY}
        />
      </Box>

      {suffix ? <Typography className={textClasses}>{suffix}</Typography> : null}
    </Box>
  );
};
